import React from 'react';
import { Smartphone, TrendingUp, Users, Clock, Gamepad2, Award } from 'lucide-react';

const Benefits = ({ language }) => {
  const texts = {
    en: {
      title: 'Discover the Best Mobile Experience',
      subtitle: 'Everything you need for an amazing mobile gaming and app experience',
      benefits: [
        {
          icon: Gamepad2,
          title: 'Popular Games',
          description: 'Access the most popular and trending mobile games loved by players everywhere.'
        },
        {
          icon: TrendingUp,
          title: 'Latest Releases',
          description: 'Stay up to date with the newest game releases and app updates as they become available.'
        },
        {
          icon: Users,
          title: 'Community Favorite',
          description: 'Join millions of users who trust our platform for their mobile entertainment needs.'
        },
        {
          icon: Clock,
          title: 'Always Available',
          description: 'Browse and access games anytime, anywhere with 24/7 availability on our platform.'
        },
        {
          icon: Smartphone,
          title: 'All Devices',
          description: 'Compatible with all major mobile platforms and device types for universal access.'
        },
        {
          icon: Award,
          title: 'Quality Content',
          description: 'Curated selection of high-quality games and apps that meet our standards.'
        }
      ]
    },
    sl: {
      title: 'Odkrijte najboljšo mobilno izkušnjo',
      subtitle: 'Vse, kar potrebujete za neverjetno mobilno igralno in aplikacijsko izkušnjo',
      benefits: [
        {
          icon: Gamepad2,
          title: 'Priljubljene igre',
          description: 'Dostop do najbolj priljubljenih in priljubljenih mobilnih iger, ki jih ljubijo igralci povsod.'
        },
        {
          icon: TrendingUp,
          title: 'Najnovejše izdaje',
          description: 'Ostanite na tekočem z najnovejšimi izdajami iger in posodobitvami aplikacij, ko postanejo na voljo.'
        },
        {
          icon: Users,
          title: 'Priljubljeno v skupnosti',
          description: 'Pridružite se milijonom uporabnikov, ki zaupajo naši platformi za svoje potrebe po mobilni zabavi.'
        },
        {
          icon: Clock,
          title: 'Vedno na voljo',
          description: 'Prebrskajte in dostopajte do iger kadar koli, kjerkoli, z 24/7 dostopnostjo na naši platformi.'
        },
        {
          icon: Smartphone,
          title: 'Vse naprave',
          description: 'Združljivo z vsemi glavnimi mobilnimi platformami in tipi naprav za univerzalni dostop.'
        },
        {
          icon: Award,
          title: 'Kakovostna vsebina',
          description: 'Izbrana zbirka visokokakovostnih iger in aplikacij, ki izpolnjujejo naše standarde.'
        }
      ]
    }
  };

  const t = texts[language];

  return (
    <section id="section-4" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            {t.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {t.benefits.map((benefit, index) => (
            <div
              key={index}
              className="group bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <benefit.icon className="w-8 h-8 text-white" />
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                {benefit.title}
              </h3>
              
              <p className="text-gray-600 leading-relaxed">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>

        {/* Games Collection Image */}
        <div className="mt-20 relative">
          <div className="relative max-w-5xl mx-auto">
            {/* Background decoration */}
            <div className="absolute -inset-8 bg-gradient-to-br from-blue-100 via-indigo-100 to-blue-100 rounded-3xl opacity-50"></div>
            
            <div className="relative bg-white rounded-3xl p-8 shadow-2xl">
              <div className="grid md:grid-cols-5 gap-6 items-center mb-6">
                <div className="md:col-span-2 space-y-3">
                  <div className="inline-flex items-center space-x-2 bg-blue-600 text-white rounded-full px-4 py-2">
                    <Award className="w-5 h-5" />
                    <span className="text-sm font-semibold">
                      {language === 'en' ? 'Curated Collection' : 'Izbrana zbirka'}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {language === 'en' ? 'Popular Games Library' : 'Knjižnica priljubljenih iger'}
                  </h3>
                  <p className="text-gray-600">
                    {language === 'en' 
                      ? 'Explore thousands of handpicked games and apps, all verified and ready to start.'
                      : 'Raziščite na tisoče izbranih iger in aplikacij, vse preverjene in pripravljene za začetek.'}
                  </p>
                </div>
                
                <div className="md:col-span-3 relative">
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-blue-600 rounded-full opacity-10 blur-2xl"></div>
                  <img
                    src="/images/games-collection.jpeg"
                    alt={language === 'en' ? 'Collection of popular mobile games' : 'Zbirka priljubljenih mobilnih iger'}
                    className="w-full h-auto rounded-2xl shadow-xl transform hover:scale-[1.02] transition-transform duration-500"
                  />
                  
                  {/* Floating stats */}
                  <div className="absolute -bottom-4 -left-4 bg-white rounded-xl px-4 py-3 shadow-xl">
                    <div className="text-2xl font-bold text-blue-600">10K+</div>
                    <div className="text-xs text-gray-600">
                      {language === 'en' ? 'Active Users' : 'Aktivnih uporabnikov'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Benefits;
